import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Storefront as StorefrontIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { LineChartComponent } from '../../components/charts';
import showcaseAnalyticsService, { type ShowcaseAnalyticsSummary } from '../../services/showcaseAnalyticsService';

type Preset = '7' | '30' | '90' | 'custom';

const today = () => format(new Date(), 'yyyy-MM-dd');
const daysAgo = (n: number) => format(subDays(new Date(), n - 1), 'yyyy-MM-dd');
const fmtInt = (n: number) => n.toLocaleString('pt-BR');
const fmtPct = (part: number, total: number) =>
  total > 0 ? `${((part / total) * 100).toFixed(1).replace('.', ',')}%` : '—';
const fmtCurrency = (v: number | null) =>
  v ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }) : '—';
const fmtDay = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const SOURCE_LABELS: Record<string, string> = {
  direto: 'Acesso direto / WhatsApp',
  'instagram.com': 'Instagram',
  instagram: 'Instagram',
  'facebook.com': 'Facebook',
  facebook: 'Facebook',
  'google.com': 'Google',
  'google.com.br': 'Google',
  google: 'Google',
};
const DEVICE_LABELS: Record<string, string> = {
  mobile: 'Celular',
  desktop: 'Computador',
  tablet: 'Tablet',
  desconhecido: 'Desconhecido',
};
const STATUS_LABELS: Record<string, { label: string; color: 'success' | 'default' | 'warning' }> = {
  available: { label: 'À venda', color: 'success' },
  sold: { label: 'Vendido', color: 'default' },
  maintenance: { label: 'Manutenção', color: 'warning' },
  deleted: { label: 'Excluído', color: 'default' },
};

const StatCard: React.FC<{ label: string; value: string; hint?: string; accent?: boolean }> = ({
  label,
  value,
  hint,
  accent,
}) => (
  <Paper sx={{ p: 2.5, height: '100%' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700, color: accent ? 'primary.main' : 'text.primary' }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Paper>
);

const RankList: React.FC<{ title: string; rows: { label: string; value: number }[]; unit: string; empty: string }> = ({
  title,
  rows,
  unit,
  empty,
}) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <Paper sx={{ p: 2.5, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      ) : (
        rows.map((r) => (
          <Box key={r.label} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" noWrap title={r.label}>
                {r.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {fmtInt(r.value)} {unit}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={(r.value / max) * 100} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        ))
      )}
    </Paper>
  );
};

const VitrineAnalyticsPage: React.FC = () => {
  const [preset, setPreset] = useState<Preset>('30');
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(today());
  const [data, setData] = useState<ShowcaseAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await showcaseAnalyticsService.getSummary(dateFrom, dateTo));
    } catch (e: any) {
      setError(
        e?.response?.status === 404
          ? 'O servidor ainda não tem as estatísticas da vitrine. Publique a nova versão da API e rode as migrations.'
          : 'Não foi possível carregar as estatísticas da vitrine.'
      );
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePreset = (_: unknown, value: Preset | null) => {
    if (!value) return;
    setPreset(value);
    if (value !== 'custom') {
      setDateFrom(daysAgo(Number(value)));
      setDateTo(today());
    }
  };

  const chartData = useMemo(
    () => ({
      labels: (data?.daily || []).map((d) => fmtDay(d.date)),
      values: (data?.daily || []).map((d) => d.visitors),
      label: 'Visitantes',
    }),
    [data]
  );

  const t = data?.totals;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <StorefrontIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4">Acessos da Vitrine</Typography>
            <Typography variant="body2" color="text.secondary">
              Quem visitou o catálogo público, quais carros chamam atenção e de onde vêm os clientes.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {t && <Chip color="success" variant="outlined" label={`${fmtInt(t.online_now)} online agora`} />}
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            component="a"
            href="/vitrine"
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir vitrine
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
            Atualizar
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup size="small" exclusive value={preset} onChange={handlePreset}>
          <ToggleButton value="7">7 dias</ToggleButton>
          <ToggleButton value="30">30 dias</ToggleButton>
          <ToggleButton value="90">90 dias</ToggleButton>
          <ToggleButton value="custom">Personalizado</ToggleButton>
        </ToggleButtonGroup>
        {preset === 'custom' && (
          <>
            <TextField
              size="small"
              type="date"
              label="De"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="Até"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data && t ? (
        <>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCard label="Visitantes únicos" value={fmtInt(t.visitors)} hint={`${fmtInt(t.sessions)} visitas`} accent />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Páginas vistas"
                value={fmtInt(t.page_views)}
                hint={t.sessions ? `${(t.page_views / t.sessions).toFixed(1).replace('.', ',')} por visita` : undefined}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Carros abertos" value={fmtInt(t.vehicle_views)} hint={`${fmtInt(t.shares)} compartilhamentos`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="Cliques no WhatsApp"
                value={fmtInt(t.whatsapp_clicks)}
                hint={`${fmtPct(t.whatsapp_clicks, t.visitors)} dos visitantes`}
                accent
              />
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mb: 3 }}>
            <LineChartComponent
              data={chartData}
              title="Visitantes por dia"
              height={300}
              color="#F00000"
              backgroundColor="rgba(240, 0, 0, 0.12)"
            />
          </Paper>

          <Paper sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ p: 2.5, pb: 1 }}>
              Carros mais vistos
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Veículo</TableCell>
                    <TableCell>Situação</TableCell>
                    <TableCell align="right">Preço</TableCell>
                    <TableCell align="right">Visualizações</TableCell>
                    <TableCell align="right">Pessoas</TableCell>
                    <TableCell align="right">WhatsApp</TableCell>
                    <TableCell align="right">Conversão</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.top_vehicles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Nenhum carro foi aberto na vitrine neste período.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.top_vehicles.map((v, i) => {
                      const status = STATUS_LABELS[v.status] || { label: v.status, color: 'default' as const };
                      const name = v.brand
                        ? `${v.brand} ${v.model ?? ''} ${v.year ?? ''}`.trim()
                        : `Veículo #${v.vehicle_id}`;
                      return (
                        <TableRow key={v.vehicle_id}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>
                            {v.status === 'deleted' ? (
                              name
                            ) : (
                              <Typography
                                component={Link}
                                to={`/veiculos/${v.vehicle_id}`}
                                variant="body2"
                                sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}
                              >
                                {name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={status.label} color={status.color} variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{fmtCurrency(v.sale_price)}</TableCell>
                          <TableCell align="right">{fmtInt(v.views)}</TableCell>
                          <TableCell align="right">{fmtInt(v.unique_visitors)}</TableCell>
                          <TableCell align="right">{fmtInt(v.whatsapp_clicks)}</TableCell>
                          <TableCell align="right">{fmtPct(v.whatsapp_clicks, v.unique_visitors)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <RankList
                title="Cidades"
                unit="visitantes"
                empty="Ainda sem dados de localização."
                rows={data.cities.map((c) => ({ label: [c.city, c.region].filter(Boolean).join(' - '), value: c.visitors }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RankList
                title="Estados"
                unit="visitantes"
                empty="Ainda sem dados de localização."
                rows={data.regions.map((r) => ({ label: [r.region, r.country].filter(Boolean).join(' - '), value: r.visitors }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RankList
                title="De onde vieram"
                unit="visitas"
                empty="Sem visitas no período."
                rows={data.sources.map((s) => ({ label: SOURCE_LABELS[s.source] || s.source, value: s.sessions }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RankList
                title="Aparelhos"
                unit="visitantes"
                empty="Sem visitas no período."
                rows={data.devices.map((d) => ({ label: DEVICE_LABELS[d.device_type] || d.device_type, value: d.visitors }))}
              />
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            Visitante = mesmo navegador/aparelho. A localização é aproximada (pelo endereço de internet) e nenhum dado
            pessoal é guardado. Para saber de qual post veio o cliente, divulgue links como{' '}
            <b>/vitrine?utm_source=instagram</b>.
          </Typography>
        </>
      ) : null}
    </Box>
  );
};

export default VitrineAnalyticsPage;
